import {RsnDatePicker, RsnRadioGroup, RsnSelect, useBoolean} from '@raison/ui-kit';
import React, {FormEvent, useState} from 'react';
import {WrongDateIntervalDialog} from '~/processes/reports/ui/WrongDateIntervalDialog';
import {required, useValidateForm} from '~/processes/reports/ui/hooks/useValidateForm';
import {TransactionsRequestResult} from '../api/hooks/useReportsFormsRequests';
import {TransactionsSchema} from '../api/schemas/transactionsSchema';
import {useAccordion} from './hooks/useAccordion';
import {getIcon} from './utils/geticon';

export type DateRange = 'all-time' | 'custom-range';
export type FileType = 'csv' | 'xlsx';

export interface TransactionsForm {
    dateRange: DateRange;
    startDate?: Date;
    endDate?: Date;
    format: FileType;
    service: string;
    transaction: string;
    asset: string;
}

interface Props extends TransactionsRequestResult {
    formId: string;
}

export default function TransactionsReportForm({formId, ...rest}: Props) {
    const {options, values, setValue, onSubmit, getHelperProps, invalidRangeModal} = useTransactionsForm(rest);
    const {contentRef, containerRef} = useAccordion(values.dateRange === 'custom-range');

    return (
        <>
            <form id={formId} className='flex flex-1 flex-col' onSubmit={onSubmit}>
                <div className='mb-6 px-4'>
                    <RsnRadioGroup<DateRange>
                        label='Date range'
                        options={options.dateRanges}
                        value={values.dateRange}
                        onChange={value => setValue('dateRange', value)}
                    />
                </div>
                <div ref={containerRef} className='px-4'>
                    <div className='mb-6 flex flex-col gap-6' ref={contentRef}>
                        <RsnDatePicker
                            name='start-date'
                            label={<RsnDatePicker.Label>Start date</RsnDatePicker.Label>}
                            value={values?.startDate}
                            onChange={value => setValue('startDate', value)}
                            {...getHelperProps('startDate')}
                        />
                        <RsnDatePicker
                            name='end-date'
                            label={<RsnDatePicker.Label>End date</RsnDatePicker.Label>}
                            value={values?.endDate}
                            onChange={value => setValue('endDate', value)}
                            {...getHelperProps('endDate')}
                        />
                    </div>
                </div>
                <div className='flex flex-col gap-6 px-4'>
                    <RsnSelect<string>
                        name='service'
                        label={<RsnSelect.Label>Services</RsnSelect.Label>}
                        options={options.services}
                        onChange={value => setValue('service', value)}
                        value={values.service}
                    />
                    <RsnRadioGroup<string>
                        label='Transactions'
                        options={options.transactions}
                        value={values.transaction}
                        onChange={value => setValue('transaction', value)}
                    />
                    <RsnSelect<string>
                        name='asset'
                        label={<RsnSelect.Label>Asset</RsnSelect.Label>}
                        options={options.currencies}
                        onChange={value => setValue('asset', value)}
                        value={values.asset}
                        disabled={!options.currencies || options.currencies.length <= 1}
                    />
                    <RsnRadioGroup<FileType>
                        label='File type'
                        options={options.fileTypes}
                        value={values.format}
                        onChange={value => setValue('format', value)}
                    />
                </div>
            </form>
            <WrongDateIntervalDialog isOpen={invalidRangeModal.open} onClose={invalidRangeModal.onClose} />
        </>
    );
}

function useTransactionsForm({data, mutate}: TransactionsRequestResult) {
    const [formState, setFormState] = useState<TransactionsForm>(() => {
        const service = getServices(data)[0]?.value;
        const transaction = getTransactions(data, service)[0]?.value;
        const currency = getAssets(data, service, transaction)[0]?.value;

        return {
            service,
            transaction,
            asset: currency,
            dateRange: dateRanges[0].value,
            startDate: undefined,
            endDate: new Date(),
            format: fileTypes[0].value,
        };
    });

    const [invalidRangeModalIsOpen, {on: openInvalidRangeModal, off: closeInvalidRangeModal}] = useBoolean();

    const {signalSubmitTry, signalChangeTry, getHelperProps} = useValidateForm<TransactionsForm>(formState, {
        startDate: formState.dateRange === 'custom-range' ? [required('Required')] : undefined,
        endDate: formState.dateRange === 'custom-range' ? [required('Required')] : undefined,
    });

    const setValue = <K extends keyof TransactionsForm>(key: K, value: TransactionsForm[K]) => {
        signalChangeTry(key);
        switch (key) {
            case 'service': {
                const transaction = getTransactions(data, formState.service)[0]?.value;
                const asset = getAssets(data, formState.service, transaction)[0]?.value;
                setFormState(prevState => ({
                    ...prevState,
                    service: value as string,
                    transaction,
                    asset,
                }));
                break;
            }
            case 'transaction': {
                const transaction = value as string;
                setFormState(prevState => ({
                    ...prevState,
                    transaction,
                    asset: getAssets(data, formState.service, transaction)[0]?.value,
                }));
                break;
            }
            default: {
                setFormState(prevState => ({
                    ...prevState,
                    [key]: value,
                }));
                break;
            }
        }
    };

    const onSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (signalSubmitTry()) {
            if ((formState?.startDate || 0) > (formState?.endDate || 0)) {
                openInvalidRangeModal();
                return;
            }
            mutate(formState);
        }
    };

    return {
        values: formState,
        setValue,
        onSubmit,
        options: {
            fileTypes,
            dateRanges,
            services: getServices(data),
            transactions: getTransactions(data, formState.service),
            currencies: getAssets(data, formState.service, formState.transaction),
        },
        getHelperProps,
        invalidRangeModal: {
            open: invalidRangeModalIsOpen,
            onClose: closeInvalidRangeModal,
        },
    };
}

function getServices(data: TransactionsSchema) {
    return data.services.map(({name, key, icon}) => ({
        key,
        label: name,
        value: key,
        icon: getIcon(icon),
    }));
}

function getTransactions(data: TransactionsSchema, service: string) {
    return data.transactions
        .filter(({for_services}) => for_services.includes(service))
        .map(({name, key}) => ({
            key,
            label: name,
            value: key,
        }));
}

function getAssets(data: TransactionsSchema, service: string, transaction: string) {
    return data.assets
        .filter(
            ({for_services, for_transactions}) =>
                for_services.includes(service) && for_transactions.includes(transaction)
        )
        .map(({name, key, icon}) => ({
            key,
            label: name,
            value: key,
            icon: getIcon(icon),
        }));
}

const dateRanges: RsnRadioGroup.Option<DateRange>[] = [
    {
        label: 'All time',
        value: 'all-time',
        key: 'all-time',
    },
    {
        label: 'Custom range',
        value: 'custom-range',
        key: 'custom-range',
    },
];

const fileTypes: RsnRadioGroup.Option<FileType>[] = [
    {
        label: 'CSV',
        value: 'csv',
        key: 'csv',
    },
    {
        label: 'XLSX',
        value: 'xlsx',
        key: 'xlsx',
    },
];
